import {unitFormats} from '@/lib/StandardFields';
import {Link} from '@/lib/classes/Link';
import {v4 as uuidV4} from 'uuid';
import {Citation, CitationOptions} from '../Citation';

export type DFValue = string | number | Link | null;
export interface DisplayFieldOptions {
  label?: string;
  unit?: string;
  value?: DFValue;
  displayFields?: DisplayField[];
  allowEdit?: boolean;
  allowLabelEdit?: boolean;
  allowDelete?: boolean;
  valueOverridden?: boolean;
  pickerOptions?: string[];
  citation?: CitationOptions;
  uuid?: string;
  generatedByAI?: boolean;
}

/**
 * Describes a data field that can be displayed to a user. At least one of `value` or `displayFields` must be supplied.
 */
class DisplayField {
  label: string;
  unit: string;
  value: DFValue;
  displayFields?: DisplayField[];
  allowEdit: boolean;
  allowLabelEdit: boolean;
  allowDelete: boolean;
  valueOverridden: boolean;
  pickerOptions?: string[];
  citation: Citation | undefined;
  uuid: string;
  generatedByAI?: boolean;

  constructor({
    label = '',
    unit = 'string',
    value = null,
    displayFields,
    allowEdit = false,
    allowLabelEdit = false,
    allowDelete = false,
    pickerOptions,
    uuid,
    valueOverridden = false,
    citation,
    generatedByAI = false,
  }: DisplayFieldOptions = {}) {
    this.label = label;
    this.unit = unit;
    this.value = value;

    if (displayFields) {
      if (!Array.isArray(displayFields)) {
        throw new Error(`DisplayField: displayFields must be an array if supplied. Got "${displayFields}"`);
      }
      this.displayFields = displayFields.map((displayField) => new DisplayField(displayField));
      if (this.value !== null && this.displayFields.length) {
        console.warn(
          `DisplayField: "${this.label}" of type "${this.unit}" has a value ("${this.value}") and ${this.displayFields?.length} display field(s). Do not use value and display fields together.`,
        );
      }
    }

    this.allowEdit = allowEdit;
    this.allowLabelEdit = allowLabelEdit;
    this.allowDelete = allowDelete;
    this.valueOverridden = valueOverridden;

    if (this.unit === 'picker' || this.unit === 'multiPicker') {
      this.pickerOptions = pickerOptions || [];
    }
    if (citation) {
      this.citation = new Citation({
        ...citation,
        customDeleteHandler: citation.customDeleteHandler
          ? () => citation.customDeleteHandler?.()
          : undefined,
        customRestoreHandler: citation.customRestoreHandler
          ? () => citation.customRestoreHandler?.()
          : undefined,
      });
    }
    this.uuid = uuid || uuidV4();
    this.generatedByAI = generatedByAI;
  }

  equals(other: DisplayField): boolean {
    if (!other || !(other instanceof DisplayField)) {
      return false;
    }
    const labelEqual = this.label === other.label;
    const valueEqual = this.value === other.value;
    const citationEqual = this?.citation?.deleted === other?.citation?.deleted;
    if (this.displayFields || other.displayFields) {
      return (
        labelEqual &&
        valueEqual &&
        citationEqual &&
        this.displayFields?.length === other.displayFields?.length &&
        this.displayFields?.every((displayField, index) =>
          displayField?.equals(other.displayFields?.[index]),
        )
      );
    }
    return labelEqual && valueEqual && citationEqual;
  }

  unitFormat(): {prefix?: string; suffix?: string; maximumFractionDigits?: number} {
    if (!(this.unit in unitFormats)) {
      console.warn(
        `Unit "${this.unit}" of displayField "${this.label}" not defined in unitFormat config; using defaults.`,
      );
      return {};
    }
    return unitFormats[this.unit];
  }

  toValueString(baseValue = false): string {
    if (this.value == null) return '';
    const unitFormat = this.unitFormat();
    const prefix = baseValue ? undefined : unitFormat.prefix;
    const suffix = baseValue ? undefined : unitFormat.suffix;

    let valueAsString = this.value;
    const isNumber = unitFormat.prefix != null || unitFormat.suffix != null;
    if (isNumber) {
      const valueText = this.value.toString().replace(/,/g, '');
      if (!isNaN(Number(valueText))) {
        valueAsString = Number(valueText).toLocaleString(undefined, {
          maximumFractionDigits: unitFormat.maximumFractionDigits ?? 2,
        });
      }
    }
    if (this.value?.url) {
      valueAsString = baseValue ? this.value.url : this.value.text;
    }
    return `${prefix ?? ''}${valueAsString}${suffix ?? ''}`;
  }

  updateValue(newValue: DFValue): void {
    if (newValue === undefined) {
      throw new Error(`DisplayField: Cannot update value to undefined. Current value: ${this.value}`);
    }
    this.value = newValue;
  }
}

function displayFieldsToObjects(displayFields: DisplayField[], depth = 0): object[] | null {
  if (depth >= 4) {
    console.error(`Error while recurring, achieved depth of ${depth}`);
    return null;
  }
  const displayFieldObjects: object[] = [];
  displayFields.forEach((df) => {
    try {
      let displayFields = null;
      if (df.displayFields?.length) {
        displayFields = displayFieldsToObjects(df.displayFields, depth + 1);
      }

      let value = df.value ?? null;
      if (value instanceof Link) {
        value = {
          text: df.value.text ?? 'Click Here',
          type: df.value.type ?? 'url',
          url: df.value.url ?? null,
        };
      } else if (df.unit === 'link' && typeof value === 'string') {
        value = {
          text: 'Click Here',
          type: 'link',
          url: value,
        };
      }

      displayFieldObjects.push({
        label: df.label,
        value,
        unit: df.unit,
        allowEdit: df.allowEdit ?? false,
        allowLabelEdit: df.allowLabelEdit ?? false,
        allowDelete: df.allowDelete ?? false,
        displayFields,
        citation: df.citation
          ? {
              document: df.citation.document,
              pageNumbers: df.citation.pageNumbers,
              deleted: df.citation.deleted ?? false,
            }
          : null,
        uuid: df.uuid,
        valueOverridden: df.valueOverridden ?? false,
        pickerOptions: df.pickerOptions ?? [],
        generatedByAI: df.generatedByAI ?? false,
      });
    } catch (error) {
      console.error(
        `Error converting "${df.label}" of type ${df.unit} and value ${JSON.stringify(
          df.value,
        )} to object: ${error.message}`,
        df,
        error,
      );
    }
  });

  return displayFieldObjects;
}

export {DisplayField, displayFieldsToObjects};
